/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, useForm, useFormState } from 'react-final-form';
import { debounce, find, includes, isNil, map } from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import TextField from 'common/forms/TextField';
import DateField from 'common/forms/DateField';
import SelectField from 'common/forms/SelectField';
import RadioGroupField from 'common/forms/RadioGroupField';
import { validatePhoneNumber, validateZipCode } from 'common/forms/formValidations';
import usStateAbbreviations from 'common/constants/usStateAbbreviation';
import { casingFormat } from 'common/utils';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { getFulfillingPharmacies } from 'store/thunks/pharmacyThunks';
import { refreshAfterMutation } from 'store/thunks/shipmentModuleThunks';
import { getPatientsApi } from 'api/patient';
import { createManualShipmentApi } from 'api/shipment';
import { RiShip2Fill } from 'react-icons/ri';

import { format } from 'date-fns';
import ManualShipmentItemsSection from './ManualShipmentItemsSection';

const RECIPIENT_MODES = {
  PATIENT_SEARCH: 'PATIENT_SEARCH',
  MANUAL_ENTRY: 'MANUAL_ENTRY',
};

const ADDRESS_MODES = {
  PATIENT_ADDRESS: 'PATIENT_ADDRESS',
  CUSTOM_ADDRESS: 'CUSTOM_ADDRESS',
};

const validateShipDate = (value) => {
  if (!value) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selected = new Date(value);
  selected.setHours(0, 0, 0, 0);
  if (selected < today) return 'Must be today or a future date';
  return undefined;
};

const SHIPPING_SERVICE_OPTIONS = [
  { value: 'FEDEX_STANDARD_OVERNIGHT', label: 'FedEx Standard Overnight\u00AE' },
  { value: 'FEDEX_PRIORITY_OVERNIGHT', label: 'FedEx Priority Overnight\u00AE' },
  { value: 'FEDEX_2DAY', label: 'FedEx 2Day\u00AE' },
];

const PartnerPracticeSection = () => {
  const dispatch = useDispatch();
  const formState = useFormState();
  const partners = useSelector(({ partner, auth }) => partner[auth.currentTenantId]);
  const practices = useSelector(({ partner, auth }) => {
    const partnerRecord = find(partner[auth.currentTenantId], {
      partnerUuid: formState.values.partnerId,
    });
    return partnerRecord?.partnerPractices;
  });

  useEffect(() => {
    if (formState.values.partnerId) {
      dispatch(getPartnerPractices({ partnerIds: [formState.values.partnerId] }));
    }
  }, [dispatch, formState.values.partnerId]);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
          Partner & Practice
        </Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <SelectField
          name='partnerId'
          id='ManualShipment-partnerId'
          label='Partner'
          required
          endAdornment={isNil(partners) ? <CircularProgress sx={{ mr: 3 }} size={20} /> : undefined}
        >
          {map(partners, (partner) => (
            <MenuItem key={partner.partnerUuid} value={partner.partnerUuid}>
              {casingFormat(partner.partnerName)}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>

      <Grid item xs={12} sm={4}>
        <SelectField
          name='practiceId'
          id='ManualShipment-practiceId'
          label='Practice'
          required
          disabled={!formState.values.partnerId || isNil(practices)}
          endAdornment={
            formState.values.partnerId && isNil(practices) ? (
              <CircularProgress sx={{ mr: 3 }} size={20} />
            ) : undefined
          }
        >
          {map(practices, (practice) => (
            <MenuItem key={practice.practiceId} value={practice.practiceId}>
              {casingFormat(practice.practiceName)}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
    </>
  );
};

const PatientSearchSection = () => {
  const form = useForm();
  const { values: formValues } = useFormState({
    subscription: { values: true },
  });
  const { practiceId, addressMode } = formValues;

  const [patients, setPatients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef('');

  const getPatientDefaultAddress = useCallback(
    (patient) => find(patient?.addresses, ({ tags }) => includes(tags, 'DEFAULT'))?.address,
    []
  );

  const getPatientDefaultPhoneNumber = useCallback((patient) => {
    const defaultPhone = find(patient?.phoneNumbers, ({ tags }) => includes(tags, 'DEFAULT'));
    return defaultPhone?.phoneNumber || '';
  }, []);

  const fillAddressFromPatient = useCallback(
    (patient) => {
      const defaultAddress = getPatientDefaultAddress(patient);
      const defaultPhone = getPatientDefaultPhoneNumber(patient);
      form.change(
        'nameOnAddress',
        `${casingFormat(patient.firstName)} ${casingFormat(patient.lastName)}`
      );
      form.change('addressLine1', defaultAddress?.line1 || '');
      form.change('addressLine2', defaultAddress?.line2 || '');
      form.change('city', defaultAddress?.city || '');
      form.change('state', defaultAddress?.state || '');
      form.change('zip', defaultAddress?.zip || '');
      form.change('phone', defaultPhone || '');
    },
    [form, getPatientDefaultAddress, getPatientDefaultPhoneNumber]
  );

  const clearAddressFields = useCallback(() => {
    form.change('nameOnAddress', '');
    form.change('addressLine1', '');
    form.change('addressLine2', '');
    form.change('city', '');
    form.change('state', '');
    form.change('zip', '');
    form.change('phone', '');
  }, [form]);

  const handlePatientSearch = useMemo(
    () =>
      debounce((patientName) => {
        getPatientsApi({
          filter: { name: patientName, practiceId },
          sortBy: { sortKey: 'firstName', sortOrder: 'ASC' },
          page: 1,
        })
          .then(({ patients: results }) => {
            if (inputRef.current === patientName) {
              setPatients(results || []);
            }
          })
          .finally(() => setLoading(false));
      }, 500),
    [practiceId]
  );

  const handleInputChange = useCallback(
    (_, value) => {
      setInputValue(value);
      inputRef.current = value;
      if (value) {
        setLoading(true);
        handlePatientSearch(value);
      } else {
        setPatients([]);
      }
    },
    [handlePatientSearch]
  );

  const handleSelect = useCallback(
    (_, patient) => {
      if (!patient) {
        setSelectedPatient(null);
        form.change('selectedPatientId', undefined);
        form.change('addressMode', ADDRESS_MODES.PATIENT_ADDRESS);
        clearAddressFields();
        return;
      }

      setSelectedPatient(patient);
      form.change('selectedPatientId', patient.mpi);
      form.change('addressMode', ADDRESS_MODES.PATIENT_ADDRESS);
      fillAddressFromPatient(patient);
    },
    [form, fillAddressFromPatient, clearAddressFields]
  );

  const handleAddressModeChange = useCallback(
    (event) => {
      const mode = event.target.value;
      form.change('addressMode', mode);
      if (selectedPatient) {
        fillAddressFromPatient(selectedPatient);
      } else if (mode === ADDRESS_MODES.CUSTOM_ADDRESS) {
        clearAddressFields();
      }
    },
    [form, selectedPatient, fillAddressFromPatient, clearAddressFields]
  );

  const isSearchDisabled = !practiceId;
  const isPatientAddress = addressMode === ADDRESS_MODES.PATIENT_ADDRESS;

  return (
    <>
      <Grid item xs={12}>
        <Autocomplete
          disabled={isSearchDisabled}
          options={patients}
          loading={loading}
          value={selectedPatient}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleSelect}
          getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
          isOptionEqualToValue={(option, value) => option.mpi === value.mpi}
          filterOptions={(options) => options}
          noOptionsText={inputValue ? 'No patients found' : 'Type to search'}
          renderOption={(props, patient) => {
            const defaultAddress = getPatientDefaultAddress(patient);
            const addressText = defaultAddress
              ? `${defaultAddress.line1}, ${defaultAddress.city}, ${defaultAddress.state} ${defaultAddress.zip}`
              : '';

            return (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={patient.mpi}>
                <ListItemText
                  primary={`${patient.firstName} ${patient.lastName}`}
                  secondary={`DOB: ${patient.dob}${addressText ? ` | ${addressText}` : ''}`}
                />
              </li>
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label='Search By Patient Name'
              helperText={isSearchDisabled ? 'Select a partner and practice first' : ''}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>

      {selectedPatient && (
        <>
          <Grid item xs={12}>
            <FormLabel component='legend' sx={{ mb: 1 }}>
              Shipping Address
            </FormLabel>
            <RadioGroup row value={addressMode} onChange={handleAddressModeChange}>
              <FormControlLabel
                value={ADDRESS_MODES.PATIENT_ADDRESS}
                control={<Radio size='small' />}
                label='Patient Address'
              />
              <FormControlLabel
                value={ADDRESS_MODES.CUSTOM_ADDRESS}
                control={<Radio size='small' />}
                label='Custom Address'
              />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name='nameOnAddress'
              id='ManualShipment-nameOnAddress'
              label='Recipient Name'
              required
              disabled={isPatientAddress}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name='addressLine1'
              id='ManualShipment-addressLine1'
              label='Address Line 1'
              required
              disabled={isPatientAddress}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name='addressLine2'
              id='ManualShipment-addressLine2'
              label='Address Line 2'
              disabled={isPatientAddress}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name='city'
              id='ManualShipment-city'
              label='City'
              required
              disabled={isPatientAddress}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SelectField
              name='state'
              id='ManualShipment-state'
              label='State'
              required
              disabled={isPatientAddress}
            >
              {map(usStateAbbreviations, ({ name, abbreviation }) => (
                <MenuItem key={abbreviation} value={abbreviation}>
                  {name}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name='zip'
              id='ManualShipment-zip'
              label='Zip Code'
              required
              disabled={isPatientAddress}
              validations={[validateZipCode('Invalid zip code')]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name='phone'
              id='ManualShipment-phone'
              label='Phone Number'
              disabled={isPatientAddress}
              required
              validations={[validatePhoneNumber()]}
            />
          </Grid>
        </>
      )}
    </>
  );
};

const MAX_WEIGHT_LBS = 50;

const validateWeight = (value, allValues) => {
  if (!value && value !== 0) return 'Required';
  const lbs = parseFloat(value) || 0;
  const oz = parseFloat(allValues?.weightOz) || 0;
  const totalWeightLbs = lbs + oz / 16;
  if (totalWeightLbs <= 0) return 'Weight is required';
  if (totalWeightLbs > MAX_WEIGHT_LBS) return `Cannot exceed ${MAX_WEIGHT_LBS} LB`;
  return undefined;
};

const WeightSection = () => {
  const form = useForm();
  const { values: formValues } = useFormState({ subscription: { values: true } });
  const weightLbs = formValues.weightLbs || '';
  const weightOz = formValues.weightOz || '';

  const handleIncrementLbs = () => {
    const current = parseFloat(weightLbs) || 0;
    form.change('weightLbs', (current + 1).toString());
  };

  const handleDecrementLbs = () => {
    const current = parseFloat(weightLbs) || 0;
    if (current > 0) {
      form.change('weightLbs', (current - 1).toString());
    }
  };

  const handleIncrementOz = () => {
    const current = parseFloat(weightOz) || 0;
    if (current < 15) {
      form.change('weightOz', (current + 1).toString());
    } else {
      const lbs = parseFloat(weightLbs) || 0;
      form.change('weightLbs', (lbs + 1).toString());
      form.change('weightOz', '0');
    }
  };

  const handleDecrementOz = () => {
    const current = parseFloat(weightOz) || 0;
    if (current > 0) {
      form.change('weightOz', (current - 1).toString());
    } else {
      const lbs = parseFloat(weightLbs) || 0;
      if (lbs > 0) {
        form.change('weightLbs', (lbs - 1).toString());
        form.change('weightOz', '15');
      }
    }
  };

  const arrowButtonSx = {
    p: 0,
    height: '14px',
    width: '20px',
    minWidth: '20px',
  };

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
        Weight <span>*</span>
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Field
          name='weightLbs'
          validate={validateWeight}
          subscription={{ error: true, touched: true, submitFailed: true }}
        >
          {({ meta }) => {
            const showError = (meta.touched || meta.submitFailed) && !!meta.error;
            return (
              <MuiTextField
                type='number'
                value={weightLbs}
                onChange={(e) => form.change('weightLbs', e.target.value)}
                onBlur={() => form.blur('weightLbs')}
                error={showError}
                helperText={showError ? meta.error : ''}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input': { textAlign: 'right', fontWeight: 200 },
                  '& .MuiInputBase-root': { height: 40 },
                }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>lbs</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position='end' sx={{ mr: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                        }}
                      >
                        <IconButton size='small' onClick={handleIncrementLbs} sx={arrowButtonSx}>
                          <ArrowDropUpIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={handleDecrementLbs}
                          disabled={parseFloat(weightLbs) <= 0}
                          sx={arrowButtonSx}
                        >
                          <ArrowDropDownIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0 },
                }}
              />
            );
          }}
        </Field>
        <MuiTextField
          type='number'
          value={weightOz}
          onChange={(e) => {
            form.change('weightOz', e.target.value);
            form.blur('weightLbs');
          }}
          sx={{
            flex: 1,
            '& .MuiInputBase-input': { textAlign: 'right', fontWeight: 200 },
            '& .MuiInputBase-root': { height: 40 },
          }}
          InputProps={{
            startAdornment: <InputAdornment position='start'>oz</InputAdornment>,
            endAdornment: (
              <InputAdornment position='end' sx={{ mr: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <IconButton
                    size='small'
                    onClick={handleIncrementOz}
                    disabled={parseFloat(weightOz) > 15}
                    sx={arrowButtonSx}
                  >
                    <ArrowDropUpIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={handleDecrementOz}
                    disabled={parseFloat(weightOz) <= 0 && parseFloat(weightLbs) <= 0}
                    sx={arrowButtonSx}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
            inputProps: { min: 0, max: 15 },
          }}
        />
      </Box>
    </Grid>
  );
};

const CreateManualShipmentDialog = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [itemCount, setItemCount] = useState(1);

  const dispatch = useDispatch();
  const { fulfillingPharmacies } = useSelector(({ auth }) => auth);
  const pharmacyData = useSelector(({ pharmacy, auth }) => pharmacy[auth.currentTenantId]);
  const partners = useSelector(({ partner, auth }) => partner[auth.currentTenantId]);
  const hasSingleFacility = fulfillingPharmacies?.length === 1;

  useEffect(() => {
    if (!pharmacyData) {
      dispatch(getFulfillingPharmacies());
    }
  }, [dispatch, pharmacyData]);

  const handleCloseModal = (_, reason) => {
    if (reason !== 'backdropClick') {
      setItemCount(1);
      onClose();
    }
  };

  const handleSubmit = (formData) => {
    // Validate at least 1 item
    const items = (formData.items || []).slice(0, itemCount);
    if (items.length === 0) {
      enqueueSnackbar('At least one item is required', { variant: 'error' });
      return;
    }

    const lbs = parseFloat(formData.weightLbs) || 0;
    const oz = parseFloat(formData.weightOz) || 0;
    const totalWeightLbs = lbs + oz / 16;

    const itemWeightSum = items.reduce((sum, item) => {
      const unitWt = parseFloat(item.unitWeight) || 0;
      const qty = parseInt(item.quantity, 10) || 0;
      return sum + unitWt * qty;
    }, 0);

    if (itemWeightSum > 0 && Math.abs(itemWeightSum - totalWeightLbs) > 0.01) {
      enqueueSnackbar(
        `Line item weights total ${itemWeightSum.toFixed(2)} LB but shipment weight is ${totalWeightLbs.toFixed(2)} LB`,
        { variant: 'warning' }
      );
    }

    setIsLoading(true);

    const selectedPharmacy = pharmacyData?.find(
      (p) => p.fulfillingPharmacyId === formData.fulfillingPharmacyId
    );

    const accountNumber = find(
      selectedPharmacy?.shipmentConfig?.shipmentProviders,
      (shipmentProvider) => shipmentProvider.spType === 'FEDEX'
    )?.storeId;

    const fulfillingPharmacyAddress = {
      line1: selectedPharmacy?.line1 || '',
      ...(selectedPharmacy?.line2 && { line2: selectedPharmacy.line2 }),
      city: selectedPharmacy?.city || '',
      state: selectedPharmacy?.state || '',
      zip: selectedPharmacy?.zip || '',
    };

    const shipmentItems = map(items, (item) => ({
      name: item.description,
      quantity: parseInt(item.quantity, 10),
      weight: {
        value: parseFloat(item.unitWeight) || 0,
        units: 'LB',
      },
      ...(item.skuOrReference && { sku: item.skuOrReference }),
    }));

    const input = {
      isManualShipment: true,
      signatureRequired: !!formData.signatureRequired,
      referenceId: formData.orderId,
      carrier: 'FEDEX',
      carrierServiceCode: formData.shipmentType,
      shippingAddress: {
        address: {
          line1: formData.addressLine1,
          ...(formData.addressLine2 && { line2: formData.addressLine2 }),
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
        },
        nameOnAddress: formData.nameOnAddress,
        phoneNumber: formData.phone,
      },
      orderWeight: {
        value: totalWeightLbs,
        units: 'LB',
      },
      items: shipmentItems,
      senderInfo: {
        name: selectedPharmacy?.name || '',
        phoneNumber: selectedPharmacy?.phone || '',
        accountNumber: accountNumber || '',
        address: fulfillingPharmacyAddress,
      },
      clientMetadata: {
        fulfillingPharmacyId: formData.fulfillingPharmacyId,
        name: selectedPharmacy?.name || '',
        pharmacyId: selectedPharmacy?.pharmacyId || '',
        phone: selectedPharmacy?.phone || '',
        npi: selectedPharmacy?.npi || '',
        pharmacist: selectedPharmacy?.pharmacist || '',

        ...(formData.partnerId &&
          (() => {
            const partner = find(partners, { partnerUuid: formData.partnerId });
            return {
              partnerId: formData.partnerId,
              ...(partner?.partnerName && { partnerName: partner.partnerName }),
            };
          })()),
        ...(formData.practiceId &&
          (() => {
            const partner = find(partners, { partnerUuid: formData.partnerId });
            const practice = find(partner?.partnerPractices, { practiceId: formData.practiceId });
            return {
              practiceId: formData.practiceId,
              ...(practice?.practiceName && { practiceName: practice.practiceName }),
            };
          })()),
        ...(formData.shipDate && { shipDate: format(formData.shipDate, 'yyyy-MM-dd') }),
        ...(formData.insuranceAmount && { insuranceAmountInUSD: Number(formData.insuranceAmount) }),
      },
    };

    createManualShipmentApi(input)
      .then(({ referenceId }) => {
        enqueueSnackbar(`Shipment created successfully: ${referenceId}`, {
          variant: 'success',
          action: (snackbarId) => (
            <>
              <IconButton
                size='small'
                color='inherit'
                title='Copy reference ID'
                onClick={() => navigator.clipboard.writeText(referenceId)}
              >
                <ContentCopyIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                color='inherit'
                aria-label='close'
                onClick={() => closeSnackbar(snackbarId)}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </>
          ),
        });
        dispatch(refreshAfterMutation());
        handleCloseModal();
      })
      .catch((error) => {
        const errorCode = error?.response?.errors?.[0]?.extensions?.code;

        // smartphos-core's relay collapses every prx-shipment-service failure's extensions.code
        // down to PRX_SHIPMENT_ERROR before it gets here, so this specific-code check can no
        // longer match -- but the original message text survives the relay, so fall back to it
        // via getGraphQLErrorMessage instead of a flat, unexplained "Failed to create shipment".
        enqueueSnackbar(
          errorCode === 'ADDRESS_EXCEEDS_FEDEX_LABEL_LIMITS'
            ? 'This address is invalid. Please change the address before generating a shipping label.'
            : getGraphQLErrorMessage(error),
          { variant: 'error' }
        );
      })
      .finally(() => setIsLoading(false));
  };

  const initialValues = useMemo(
    () => ({
      recipientMode: RECIPIENT_MODES.MANUAL_ENTRY,
      addressMode: ADDRESS_MODES.PATIENT_ADDRESS,
      shipmentType: 'FEDEX_STANDARD_OVERNIGHT',
      ...(hasSingleFacility && {
        fulfillingPharmacyId: fulfillingPharmacies[0].fulfillingPharmacyId,
      }),
    }),
    [hasSingleFacility, fulfillingPharmacies]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      aria-describedby='CreateManualShipment-Dialog-header'
      fullWidth
      maxWidth='lg'
    >
      <DialogTitle
        id='CreateManualShipment-Dialog-header'
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <RiShip2Fill size={24} />
        Add Shipment
      </DialogTitle>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit: onSubmit, invalid, values }) => (
          <form noValidate onSubmit={onSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                {/* Section A: Facility */}
                <Grid item xs={12}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                    Facility
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <SelectField
                    name='fulfillingPharmacyId'
                    id='ManualShipment-fulfillingPharmacyId'
                    label='Facility'
                    required
                    disabled={hasSingleFacility}
                  >
                    {map(
                      fulfillingPharmacies,
                      ({ fulfillingPharmacyId, fulfillingPharmacyName }) => (
                        <MenuItem key={fulfillingPharmacyId} value={fulfillingPharmacyId}>
                          {fulfillingPharmacyName}
                        </MenuItem>
                      )
                    )}
                  </SelectField>
                </Grid>

                {/* Section B: Partner & Practice */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <PartnerPracticeSection />

                {/* Section C: Recipient */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                    Recipient
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroupField
                    name='recipientMode'
                    id='ManualShipment-recipientMode'
                    label='Recipient Mode'
                    row
                    required
                  >
                    <FormControlLabel
                      value={RECIPIENT_MODES.PATIENT_SEARCH}
                      control={<Radio />}
                      label='Search Patient'
                    />
                    <FormControlLabel
                      value={RECIPIENT_MODES.MANUAL_ENTRY}
                      control={<Radio />}
                      label='Manual Entry'
                    />
                  </RadioGroupField>
                </Grid>

                {values.recipientMode === RECIPIENT_MODES.PATIENT_SEARCH && (
                  <PatientSearchSection />
                )}

                {values.recipientMode === RECIPIENT_MODES.MANUAL_ENTRY && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name='nameOnAddress'
                        id='ManualShipment-nameOnAddress'
                        label='Recipient Name'
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name='addressLine1'
                        id='ManualShipment-addressLine1'
                        label='Address Line 1'
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name='addressLine2'
                        id='ManualShipment-addressLine2'
                        label='Address Line 2'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField name='city' id='ManualShipment-city' label='City' required />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <SelectField name='state' id='ManualShipment-state' label='State' required>
                        {map(usStateAbbreviations, ({ name, abbreviation }) => (
                          <MenuItem key={abbreviation} value={abbreviation}>
                            {name}
                          </MenuItem>
                        ))}
                      </SelectField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        name='zip'
                        id='ManualShipment-zip'
                        label='Zip Code'
                        required
                        validations={[validateZipCode('Invalid zip code')]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name='phone'
                        id='ManualShipment-phone'
                        label='Phone Number'
                        required
                        validations={[validatePhoneNumber()]}
                      />
                    </Grid>
                  </>
                )}

                {/* Section D: Shipment Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                    Shipment Details
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField name='orderId' id='ManualShipment-orderId' label='Order ID' />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <SelectField
                    name='shipmentType'
                    id='ManualShipment-shipmentType'
                    label='Shipping Method'
                    required
                  >
                    {map(SHIPPING_SERVICE_OPTIONS, ({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    name='insuranceAmount'
                    id='ManualShipment-insuranceAmount'
                    label='Insurance Amount'
                    startAdornment='$'
                    format={(val) =>
                      val ? val.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1') : val
                    }
                    params={{ inputProps: { inputMode: 'decimal' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <DateField
                    name='shipDate'
                    id='ManualShipment-shipDate'
                    label='Ship Date'
                    disablePast
                    validations={[validateShipDate]}
                  />
                </Grid>

                <WeightSection />

                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Field name='signatureRequired' type='checkbox'>
                    {({ input }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!input.checked}
                            onChange={input.onChange}
                            name={input.name}
                          />
                        }
                        label='Signature Required'
                      />
                    )}
                  </Field>
                </Grid>

                {/* Section E: Items */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <ManualShipmentItemsSection
                    itemCount={itemCount}
                    onItemCountChange={setItemCount}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                Cancel
              </Button>
              <LoadingButton
                loading={isLoading}
                variant='contained'
                type='submit'
                disabled={invalid || itemCount === 0}
              >
                Add Shipment
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

CreateManualShipmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateManualShipmentDialog;
